import { useEffect, useMemo, useState } from 'react';
import { managerApi } from '../../lib/api';
import { fmtDateBR, fmtHM } from '../../lib/format';
import { urlToDataUrl } from '../../lib/photo';
import { generateReceiptPDF, generateAlimReceiptsPDF, generateAlimReceiptsPDFsZip, type AlimGroup } from '../../lib/pdf';
import { downloadCSV } from '../../lib/csv';

function exportStamp(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}${mm}_${hh}${mi}`;
}
function slug(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function AlimLogTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [camarotes, setCamarotes] = useState<string[]>([]);
  const [camarote, setCamarote] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([managerApi.getAlimLog(), managerApi.getConfig()]).then(([log, cfg]) => {
      setRows(log.rows.sort((a: any, b: any) => b.date.localeCompare(a.date)));
      setCamarotes(cfg.config.camarotes);
    }).finally(() => setLoading(false));
  }, []);

  const filteredRows = useMemo(() => camarote === 'todos' ? rows : rows.filter((r) => r.camarote === camarote), [rows, camarote]);

  async function downloadPDF(row: any) {
    const foto = row.fotoUrl ? await urlToDataUrl(row.fotoUrl) : null;
    generateReceiptPDF('Comprovante de Vale-Alimentação', { nome: row.nome, cpf: row.cpf, funcao: row.funcao, camarote: row.camarote }, {
      Data: fmtDateBR(row.date), Hora: fmtHM(row.hora), Localização: row.lat ? `${row.lat}, ${row.lng}` : '—',
      'Verificação em cadeia': (row.hashAnterior || '').slice(0, 20) + '…',
    }, foto, row.hash);
  }

  async function buildGroups(): Promise<AlimGroup[]> {
    const profiles = (await managerApi.getProfiles()).profiles;
    const sigMap = new Map(profiles.map((p: any) => [p.id, p.signatureUrl]));
    const byPerson = new Map<string, AlimGroup>();
    for (const r of filteredRows) {
      if (!byPerson.has(r.employeeId)) {
        byPerson.set(r.employeeId, { employeeId: r.employeeId, emp: { nome: r.nome, cpf: r.cpf, funcao: r.funcao, camarote: r.camarote }, entries: [], signatureImg: null });
      }
      const fotoImg = r.fotoUrl ? await urlToDataUrl(r.fotoUrl) : null;
      byPerson.get(r.employeeId)!.entries.push({ date: r.date, hora: r.hora, lat: r.lat, lng: r.lng, fotoImg });
    }
    const groups = [...byPerson.values()];
    for (const g of groups) {
      const url = sigMap.get(g.employeeId);
      g.signatureImg = url ? await urlToDataUrl(url) : null;
    }
    groups.sort((a, b) => a.emp.nome.localeCompare(b.emp.nome));
    return groups;
  }

  async function exportUnified() {
    if (filteredRows.length === 0) { alert('Nenhum registro para exportar.'); return; }
    setExporting(true);
    try {
      const groups = await buildGroups();
      const nomeArquivo = `alimunificado_${slug(camarote === 'todos' ? 'todos' : camarote)}_${groups.length}funcionarios_${exportStamp()}.pdf`;
      await generateAlimReceiptsPDF(groups, nomeArquivo);
    } finally {
      setExporting(false);
    }
  }
  async function exportIndividual() {
    if (filteredRows.length === 0) { alert('Nenhum registro para exportar.'); return; }
    setExporting(true);
    try {
      const groups = await buildGroups();
      const nomeArquivo = `alimindividual_${slug(camarote === 'todos' ? 'todos' : camarote)}_${groups.length}funcionarios_${exportStamp()}.zip`;
      await generateAlimReceiptsPDFsZip(groups, nomeArquivo);
    } finally {
      setExporting(false);
    }
  }

  function exportCSV() {
    const lines = ['Data,Funcionario,Camarote,Hora'];
    filteredRows.forEach((r) => lines.push(`${r.date},${r.nome},${r.camarote},${fmtHM(r.hora)}`));
    downloadCSV(lines.join('\n'), 'vale-alimentacao.csv');
  }

  if (loading) return <div className="card"><p className="muted">Carregando…</p></div>;

  return (
    <div className="card">
      <div className="toolbar"><h2 style={{ margin: 0 }}>Vale-alimentação registrado</h2></div>
      <label>Camarote</label>
      <select value={camarote} onChange={(e) => setCamarote(e.target.value)}>
        <option value="todos">Todos os camarotes</option>
        {camarotes.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1 }} disabled={exporting} onClick={exportIndividual}>Exportar por funcionário (.zip)</button>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} disabled={exporting} onClick={exportUnified}>Exportar tudo (PDF único)</button>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={exportCSV}>Exportar CSV</button>
      </div>
      <div className="table-wrap" style={{ marginTop: 14 }}>
        <table>
          <thead><tr><th>Data</th><th>Funcionário</th><th>Camarote</th><th>Hora</th><th>Comprovante</th></tr></thead>
          <tbody>
            {filteredRows.length === 0 && <tr><td colSpan={5} className="muted">Nenhum registro ainda.</td></tr>}
            {filteredRows.map((r, i) => (
              <tr key={i}>
                <td>{fmtDateBR(r.date)}</td>
                <td>{r.nome}</td>
                <td>{r.camarote}</td>
                <td className="mono">{fmtHM(r.hora)}</td>
                <td><button className="btn btn-ghost btn-sm" onClick={() => downloadPDF(r)}>PDF</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
